import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { State } from "state";
import { clearUploads } from "state/uploading-state";
import { clearStoreUploads } from "state/store-upload-state";

interface UploadProgressBarProps {
  offset?: number;
}

interface FileItem {
  id: string;
  name: string;
}

interface UploadState {
  uploadedFiles: string[] | FileItem[];
  uploadProgress: Record<string, number>;
  project?: any;
}

export default function UploadProgressBar({ offset = 20 }: UploadProgressBarProps) {
  const uploadingState: UploadState = useSelector((state: State) => state.uploading);
  const storeUploadState: UploadState = useSelector((state: State) => state.storeUpload);

  const [show, setShow] = useState(true);
  const dispatch = useDispatch();

  const normalizeFiles = (files: string[] | FileItem[]) =>
    files?.map(f => (typeof f === "string" ? { id: f, name: f } : f)) || [];

  // Merge both states into a single array
  const allFiles = [...normalizeFiles(uploadingState.uploadedFiles), ...normalizeFiles(storeUploadState.uploadedFiles)];
  const allProgress = allFiles.map(file => {
    if (uploadingState.uploadedFiles?.some(f => (typeof f === "string" ? f === file.id : f.id === file.id))) {
      return uploadingState.uploadProgress[file.id] || 0;
    } else if (storeUploadState.uploadedFiles?.some(f => (typeof f === "string" ? f === file.id : f.id === file.id))) {
      return storeUploadState.uploadProgress[file.id] || 0;
    }
    return 0;
  });

  const totalFiles = allFiles.length;
  const completedCount = allProgress.filter(p => p >= 100).length;
  const overallProgress = totalFiles > 0 ? Math.round(allProgress.reduce((a, b) => a + b, 0) / totalFiles) : 0;

  useEffect(() => {
    if (allFiles.length > 0) {
      setShow(true);
    }
  }, [allFiles.length]);

  // Show warning on page reload if uploads exist
  useEffect(() => {
    // Only add beforeunload listener if uploads are not complete
    if (overallProgress >= 100) return; // skip listener once complete

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (allFiles.length > 0 && overallProgress < 100) {
        e.preventDefault();
        // e.returnValue = "You have ongoing uploads. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [allFiles, overallProgress]);


  useEffect(() => {
    if (totalFiles === 0) return;

    if (overallProgress >= 100) {
      const timer = setTimeout(() => {
        setShow(false); // hide the bar
        dispatch(clearUploads());
        dispatch(clearStoreUploads());
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [overallProgress, totalFiles, dispatch]);

  // Find the current file being uploaded
  const currentFile = allFiles.find((file, index) => allProgress[index] < 100) || allFiles[allFiles.length - 1];
  const currentProgress = currentFile ? allProgress[allFiles.indexOf(currentFile)] : 0;

  // Auto-hide when all uploads are completed
  useEffect(() => {
    if (completedCount === totalFiles && totalFiles > 0 && overallProgress >= 100) {
      const timer = setTimeout(() => setShow(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [completedCount, totalFiles, overallProgress]);

  if (totalFiles === 0 || !show) return null;

  const barColor = overallProgress >= 100 ? "#22C55E" : "#E5B611";

  // Determine project/chain to display
  const projectToShow = uploadingState.project || storeUploadState.project;

  return (
    <div
      style={{
        position: "fixed",
        bottom: offset,
        right: 20,
        background: "#fff",
        borderRadius: 8,
        padding: "12px 16px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        fontFamily: "sans-serif",
        zIndex: 9999,
        display: "inline-block",
        maxWidth: 400,
      }}
    >
      {overallProgress >= 100 && (
        <div
          style={{
            position: "absolute",
            top: -6,
            right: 8,
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: 18,
            color: "#666",
          }}
          onClick={() => setShow(false)}
        >
          ×
        </div>
      )}

      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
        {projectToShow?.chain?.name || "Project"} - {projectToShow?.name || ""}
      </div>

      <div style={{ fontSize: 12, marginBottom: 10, color: "#666" }}>
        {overallProgress >= 100
          ? `Completed (${totalFiles}/${totalFiles} files)`
          : `Uploading (${completedCount}/${totalFiles} files)`}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            position: "relative",
            height: 8,
            flex: 1,
            background: "#f0f0f0",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: "100%",
              width: `${overallProgress}%`,
              background: barColor,
              borderRadius: 4,
              transition: "0.3s width linear, 0.3s background-color linear",
            }}
          ></div>
        </div>

        <div
          style={{
            background: overallProgress >= 100 ? "#D1FAE5" : "#FFEDD5",
            color: overallProgress >= 100 ? "#065F46" : "#9A3412",
            padding: "2px 8px",
            fontSize: 11,
            fontWeight: 600,
            borderRadius: 6,
            whiteSpace: "nowrap",
          }}
        >
          {overallProgress >= 100 ? "Complete" : "Processing"}
        </div>
      </div>

      <div style={{ fontSize: 11, marginTop: 6, color: "#555" }}>
        {overallProgress}% completed
      </div>
    </div>
  );
}
